import React, { useState, useEffect } from 'react';
import { userService } from '../../api/userService';
import '../Common/Common.css';

function UserForm({ user, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    role: 'user',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        name: user.name || '',
        email: user.email || '',
        password: '',
        password_confirmation: '',
        role: user.role || 'user',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!user && !formData.username) {
      newErrors.username = 'Le nom d\'utilisateur est requis';
    }

    if (!formData.name) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'L\'email est invalide';
    }

    if (!user && !formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (formData.password && formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.role) {
      newErrors.role = 'Le rôle est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const dataToSend = { ...formData };
      delete dataToSend.password_confirmation;

      // If editing and password is empty, don't send password
      if (user && !dataToSend.password) {
        delete dataToSend.password;
      }

      // If editing, don't send username (it can't be changed)
      if (user) {
        delete dataToSend.username;
      }

      if (user) {
        await userService.updateUser(user.id, dataToSend);
      } else {
        await userService.createUser(dataToSend);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        alert(error.response?.data?.message || 'Échec de l\'enregistrement de l\'utilisateur');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      {!user && (
        <div className="form-group">
          <label htmlFor="username">Nom d'utilisateur *</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className={`form-control ${errors.username ? 'error' : ''}`}
            required
          />
          {errors.username && <span className="error-message">{errors.username}</span>}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="name">Nom complet *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`form-control ${errors.name ? 'error' : ''}`}
          required
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="email">Email *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`form-control ${errors.email ? 'error' : ''}`}
          required
        />
        {errors.email && <span className="error-message">{errors.email}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="role">Rôle *</label>
        <select
          id="role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          className={`form-control ${errors.role ? 'error' : ''}`}
          required
        >
          <option value="user">Utilisateur</option>
          <option value="admin">Admin</option>
        </select>
        {errors.role && <span className="error-message">{errors.role}</span>}
        <small>Les admins ont un accès complet au tableau de bord</small>
      </div>

      <div className="form-group">
        <label htmlFor="password">
          Mot de passe {user ? '(laisser vide pour conserver l\'actuel)' : '*'}
        </label>
        <input
          type="password"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          className={`form-control ${errors.password ? 'error' : ''}`}
          required={!user}
          minLength="8"
        />
        {errors.password && <span className="error-message">{errors.password}</span>}
        <small>Minimum 8 caractères</small>
      </div>

      <div className="form-group">
        <label htmlFor="password_confirmation">
          Confirmer le mot de passe {user ? '' : '*'}
        </label>
        <input
          type="password"
          id="password_confirmation"
          name="password_confirmation"
          value={formData.password_confirmation}
          onChange={handleChange}
          className={`form-control ${errors.password_confirmation ? 'error' : ''}`}
          required={!user && formData.password}
        />
        {errors.password_confirmation && (
          <span className="error-message">{errors.password_confirmation}</span>
        )}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Annuler
        </button>
        <button type="submit" disabled={loading} className="btn btn-primary">
          {loading ? 'Enregistrement...' : user ? 'Modifier l\'utilisateur' : 'Créer l\'utilisateur'}
        </button>
      </div>
    </form>
  );
}

export default UserForm;
